import { api } from '../api';

/**
 * As duas etapas da ativação da conta do paciente (tarefa 0.33).
 *
 * Rotas **públicas**: quem chama é o próprio paciente, pelo link do e-mail de boas-vindas,
 * e ele ainda não tem sessão — é justamente a senha que está criando. O que autoriza é o
 * token do convite mais o CPF e a data de nascimento, conferidos contra o cadastro.
 */

export interface LegalResponsibleData {
  name: string;
  cpf: string;
  /** `AAAA-MM-DD`, como o back exige (`Joi` de padrão, não `Joi.date()`). */
  birthday: string;
}

export interface PatientActivationConsentTerm {
  id: string;
  content: string;
  type: string;
}

export interface PatientActivationIdentity {
  name: string;
  /**
   * `true` para paciente menor de 18 sem responsável legal no cadastro — que o cadastro
   * pelo painel não coleta. A tela pede os três campos e chama `identify` outra vez.
   */
  needsLegalResponsible: boolean;
  consentTerm: PatientActivationConsentTerm | null;
}

interface IdentifyRequest {
  token: string;
  cpf: string;
  birthday: string;
  legalResponsible?: LegalResponsibleData;
}

interface ActivateRequest extends IdentifyRequest {
  password: string;
  passwordConfirmation: string;
  /**
   * O termo que a pessoa leu. Não escolhe o que será assinado: o back resolve o termo
   * aplicável e recusa quando não bate, pedindo para recarregar a página.
   */
  consentTermId: string;
}

export const identifyPatientActivation = async (
  request: IdentifyRequest,
): Promise<PatientActivationIdentity> => {
  const { data } = await api.post('/patients/activation/identify', request);

  return data;
};

export interface PatientActivationResult {
  /**
   * Sessão de paciente emitida pela ativação (0.34). Existe para **um** passo: o vínculo de
   * WhatsApp desta mesma tela, que é o que registra o paciente no `cinco_chat` enquanto o
   * aplicativo não está publicado.
   *
   * Vem `null` quando a emissão falhou no back — e nesse caso a ativação **valeu**: a conta
   * está criada e o termo assinado. A tela pula o passo e conclui.
   */
  token: string | null;
}

export const activatePatient = async (
  request: ActivateRequest,
): Promise<PatientActivationResult> => {
  const { data } = await api.post('/patients/activation', request);

  return { token: data?.token ?? null };
};
